import type { QueryClient } from '@tanstack/svelte-query';
import { createQuery } from '@tanstack/svelte-query';
import { fromStore, get, toStore, type Readable } from 'svelte/store';
import type {
	GetMerchCartQuery,
	GetMerchCartQueryVariables,
} from '../../queries/__generated__/getMerchCart.generated';
import { GetMerchCartDocument } from '../../queries/__generated__/getMerchCart.generated';
import { getMerchCartQueryVariables } from '../getMerchCartQueryVariables/getMerchCartQueryVariables';
import { FeatureFlags } from '#lib/constants/feature-flags.js';
import { NONEXISTENT_PRODUCT_ID } from '#lib/features/checkout/constants/nonexistent-product-id.js';
import type { CartItems } from '#lib/features/checkout/utils/createCartItemsStore/index.js';
import { getQuery } from '#lib/query-utils/getQuery.js';
import { getMerchCart } from '#lib/websites/merch/api/getMerchCart.js';
import { stringifyIds } from '#lib/websites/nft/utils/stringifyIds/stringifyIds.js';
import { getQueryKeys } from '$global/query-utils/getQueryKeys';
import { getImageUrl } from '$global/utils/getImageUrl';

export const getMerchCartQuery = (
	queryClient: QueryClient,
	cartItems: Readable<CartItems | undefined>
) => {
	const cartItems$ = fromStore(cartItems);

	return createQuery(() => {
		const $cartItems = cartItems$.current;
		const ids = Object.keys($cartItems || {});

		const queryClientData = queryClient.getQueriesData({});

		const existingQueryResult = ids?.length
			? queryClientData?.find(([[key, _, variables]]) => {
					return (
						key === 'getMerchCart' &&
						ids.every((id) =>
							(variables as GetMerchCartQueryVariables)?.filter?.product?.id?.[
								'_in'
							]?.includes(id)
						)
					);
				})
			: null;

		// If there's already cache entry a query fetching more editions than the current one
		if (existingQueryResult?.[1]) {
			return {
				queryKey: getQueryKeys(
					GetMerchCartDocument,
					getMerchCartQueryVariables(ids)
				),
				queryFn: () => {
					return {
						Merch_Item: (
							existingQueryResult?.[1] as GetMerchCartQuery
						).Merch_Item?.filter((merchItem) =>
							ids.includes(merchItem.product?.id as string)
						),
					};
				},
			};
		}

		const queryIds = ids?.length ? ids : [NONEXISTENT_PRODUCT_ID];
		const variables = getMerchCartQueryVariables(queryIds);

		// The query key is the GraphQL one on both paths, so the cache reuse above
		// and the layout's empty-cart seed keep working whichever path is live.
		return FeatureFlags.RestPages['Shop and merch cart hydration']
			? {
					queryKey: getQueryKeys(GetMerchCartDocument, variables),
					queryFn: async (): Promise<GetMerchCartQuery> =>
						stringifyIds(await getMerchCart(fetch, queryIds)),
				}
			: getQuery(GetMerchCartDocument, variables);
	});
};

export const formatMerchItems = (
	$merchCartQuery: {
		data: GetMerchCartQuery | undefined;
	},
	cartItems: Readable<CartItems | undefined>
) =>
	($merchCartQuery.data?.Merch_Item || []).map((merchItem) => {
		const allDefinedImage = merchItem.image ? merchItem.image : null;

		const allDefinedParentItemImage = merchItem?.parent_item?.[0]?.Merch_Item_id
			?.image
			? merchItem?.parent_item?.[0]?.Merch_Item_id?.image
			: null;

		return {
			image: (() => {
				if (allDefinedImage) {
					return {
						width: allDefinedImage.width || 0,
						height: allDefinedImage.height || 0,
						url: `${getImageUrl(allDefinedImage?.filename_disk, '512px')}`,
					};
				}

				return allDefinedParentItemImage
					? {
							width: allDefinedParentItemImage.width || 0,
							height: allDefinedParentItemImage.height || 0,
							url: `${getImageUrl(
								allDefinedParentItemImage?.filename_disk,
								'512px'
							)}`,
						}
					: null;
			})(),
			line1: merchItem?.title,
			line2: `${merchItem?.variant_title || ''}`,
			line3: '',
			dropConfigId: null,
			stockLimited: merchItem?.product?.is_stock_limited,
			restricted:
				merchItem?.product?.id &&
				get(cartItems)?.[merchItem?.product?.id]?.restricted,
			productId: merchItem?.product?.id,
			id: merchItem?.id,
			quantity:
				get(cartItems)?.[merchItem?.product?.id as string]?.quantity || 0,
			currency: merchItem?.product?.currency,
			price: merchItem?.product?.price,
			sku: merchItem?.product?.sku,
			variant: null,
			groupId: 'Merch',
			groupName: 'Merch',
		};
	});

export const createMerchCartQuery = (
	queryClient: QueryClient,
	cartItems: Readable<CartItems | undefined>,
	defaultLoading?: boolean
) => {
	const merchCartQuery = getMerchCartQuery(queryClient, cartItems);

	return toStore(() => ({
		...merchCartQuery,
		error: merchCartQuery.error || null,
		data: merchCartQuery.data || { Merch_Item: [] },
		isLoading: (() => {
			if (!merchCartQuery.data) {
				return typeof defaultLoading !== 'undefined' ? defaultLoading : false;
			}

			return merchCartQuery.isPending;
		})(),
		subtotal: Object.values(get(cartItems) || {}).reduce(
			(accumulator, cartItem) => {
				if (!cartItem.quantity || !cartItem.price) {
					return accumulator;
				}

				return accumulator + cartItem.quantity * cartItem.price;
			},
			0
		),
		formattedItems: formatMerchItems(merchCartQuery, cartItems),
	}));
};
